import { useMemo, useState, lazy, Suspense } from "react";
import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import {
  monthlyPrayerTimes,
  calculatePrayerTimes,
  PRAYER_LABELS_ID,
} from "@/lib/prayer/calc";
import {
  type Location,
  tzOffsetHours,
  tzLabel,
  getProvinsi,
  getKecamatanInKota,
  KOTA_ALL,
} from "@/data/locations";
import {
  todayISO,
  parseISO,
  MONTH_NAMES_ID,
  DAY_NAMES_SHORT_ID,
  dayOfWeek,
} from "@/lib/calendar/dates";
import { gregorianToHijri, HIJRI_MONTHS } from "@/lib/calendar/hijri";
import { displayLocationTitle } from "@/lib/prayer/copy";
import { PrayerPdfButton } from "@/components/PrayerPdfButton";
import { LocationQuickSearch } from "./LocationQuickSearch";
import { FavoriteButton } from "./FavoriteButton";
import { PrayerShareDialog } from "./PrayerShareDialog";
import { RelatedAreaGrid } from "./RelatedAreaGrid";
import { PrayerLocationToolsRail } from "./PrayerLocationToolsRail";
import { buildLocationFacts, locationPlaceJsonLd } from "@/lib/prayer/location-copy";
import { getPrayerCopy } from "@/lib/prayer/copy";
import { SourcesNote } from "@/components/SourcesNote";
import { prayerEntities } from "@/lib/seo/entities";
import { PrayerBreadcrumb } from "./PrayerBreadcrumb";
import { PrayerCrossNav } from "./PrayerCrossNav";
import { ChevronLeft, ChevronRight, CalendarDays, CalendarRange } from "lucide-react";
import { cn } from "@/lib/utils";

const YearView = lazy(() => import("./YearView").then((m) => ({ default: m.YearView })));
const BUKA_PUASA_COLS = ["imsak", "subuh", "maghrib", "isya"] as const;

/**
 * Focused "Jadwal Buka Puasa" view = waktu maghrib + supporting subuh/imsak rows.
 * Reuses `calculatePrayerTimes` so accuracy matches the main /jadwal-imsak/ pages.
 * Content is intentionally distinct (berbuka-first framing) to avoid duplicate
 * SERP results vs /jadwal-imsak/.
 */
export function BukaPuasaPage({ location }: { location: Location }) {
  const today = parseISO(todayISO())!;
  const tzH = tzOffsetHours(location.tz);
  const display = displayLocationTitle(location);
  const province = getProvinsi(location.provinceSlug);
  const [view, setView] = useState({ y: today.y, m: today.m });
  const [mode, setMode] = useState<"month" | "year">("month");

  const todayTimes = useMemo(
    () =>
      calculatePrayerTimes({
        y: today.y,
        m: today.m,
        d: today.d,
        lat: location.lat,
        lng: location.lng,
        tzHours: tzH,
      }),
    [today.y, today.m, today.d, location.lat, location.lng, tzH],
  );

  const month = useMemo(
    () =>
      monthlyPrayerTimes({
        y: view.y,
        m: view.m,
        lat: location.lat,
        lng: location.lng,
        tzHours: tzH,
      }),
    [view.y, view.m, location.lat, location.lng, tzH],
  );

  const hijri = gregorianToHijri(today.y, today.m, today.d);
  const monthName = MONTH_NAMES_ID[view.m - 1];
  const prevMonth = () => setView((v) => v.m === 1 ? { y: v.y - 1, m: 12 } : { y: v.y, m: v.m - 1 });
  const nextMonth = () => setView((v) => v.m === 12 ? { y: v.y + 1, m: 1 } : { y: v.y, m: v.m + 1 });
  const copy = getPrayerCopy("buka-puasa", location);
  const placeLd = locationPlaceJsonLd(location);

  const todayIso = `${today.y}-${String(today.m).padStart(2, "0")}-${String(today.d).padStart(2, "0")}`;
  const tzOffsetIso = (() => {
    const sign = tzH >= 0 ? "+" : "-";
    const hh = String(Math.floor(Math.abs(tzH))).padStart(2, "0");
    const mm = String(Math.round((Math.abs(tzH) % 1) * 60)).padStart(2, "0");
    return `${sign}${hh}:${mm}`;
  })();
  const canonicalPath = location.kind === "kecamatan"
    ? `/jadwal-buka-puasa/${location.parentSlug}/${location.slug}/`
    : `/jadwal-buka-puasa/${location.slug}/`;

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Beranda", item: "https://hari.co.id/" },
      { "@type": "ListItem", position: 2, name: "Jadwal Buka Puasa", item: "https://hari.co.id/jadwal-buka-puasa/" },
      ...(province ? [{
        "@type": "ListItem", position: 3, name: province.name,
        item: `https://hari.co.id/jadwal-buka-puasa/${province.slug}/`,
      }] : []),
      ...(location.kind === "kecamatan" && location.parentSlug ? [{
        "@type": "ListItem", position: province ? 4 : 3, name: location.parentSlug,
        item: `https://hari.co.id/jadwal-buka-puasa/${location.parentSlug}/`,
      }] : []),
      { "@type": "ListItem", position: province ? (location.kind === "kecamatan" ? 5 : 4) : 3, name: display,
        item: `https://hari.co.id${canonicalPath}` },
    ],
  };

  const eventLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: `Buka Puasa (Iftar) ${display}`,
    description: `Waktu berbuka puasa (Maghrib) di ${display} berdasarkan metode hisab Kementerian Agama RI.`,
    startDate: `${todayIso}T${todayTimes.maghrib}:00${tzOffsetIso}`,
    endDate: `${todayIso}T${todayTimes.isya}:00${tzOffsetIso}`,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: display,
      address: {
        "@type": "PostalAddress",
        addressLocality: location.name,
        addressRegion: province?.name ?? "Indonesia",
        addressCountry: "ID",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: location.lat,
        longitude: location.lng,
      },
    },
    about: [
      { "@type": "Thing", name: "Maghrib", sameAs: "https://en.wikipedia.org/wiki/Maghrib_prayer" },
      { "@type": "Thing", name: "Iftar", sameAs: "https://en.wikipedia.org/wiki/Iftar" },
      { "@type": "Thing", name: "Ramadhan", sameAs: "https://id.wikipedia.org/wiki/Ramadan" },
    ],
    organizer: {
      "@type": "Organization",
      name: "Kementerian Agama Republik Indonesia",
      url: "https://kemenag.go.id",
    },
    performer: {
      "@type": "Organization",
      name: "Kementerian Agama Republik Indonesia",
      url: "https://kemenag.go.id",
    },
    image: ["https://hari.co.id/og-default.svg"],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "IDR",
      availability: "https://schema.org/InStock",
      url: `https://hari.co.id${canonicalPath}`,
      validFrom: todayIso,
    },
    url: `https://hari.co.id${canonicalPath}`,
  };

  const datasetLd = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: `Jadwal Buka Puasa ${monthName} ${today.y} - ${display}`,
    description: `Jadwal harian Maghrib (waktu buka puasa), Imsak, Subuh, dan Isya untuk ${display} selama bulan ${monthName} ${today.y}.`,
    url: `https://hari.co.id${canonicalPath}`,
    creator: { "@type": "Organization", name: "hari.co.id" },
    license: "https://hari.co.id/syarat-ketentuan/",
    temporalCoverage: `${todayIso}/P1M`,
    spatialCoverage: {
      "@type": "Place",
      geo: { "@type": "GeoCoordinates", latitude: location.lat, longitude: location.lng },
    },
    measurementTechnique: "Astronomical solar position (NREL/SPA) with Kemenag RI parameters: Subuh 20°, Isya 18°, ihtiyat 2 minutes",
    variableMeasured: ["Imsak", "Subuh", "Maghrib (Iftar)", "Isya"],
  };

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Jadwal Buka Puasa ${monthName} ${view.y} - ${display}`,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: month.length,
    itemListElement: month.slice(0, 31).map((r, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: `Buka Puasa ${r.iso} di ${display}`,
      url: `https://hari.co.id${canonicalPath}`,
      item: {
        "@type": "Event",
        name: `Buka Puasa ${r.iso}`,
        description: `Waktu berbuka puasa (Maghrib) di ${display} pada ${r.iso}.`,
        startDate: `${r.iso}T${r.times.maghrib}:00${tzOffsetIso}`,
        endDate: `${r.iso}T${r.times.isya}:00${tzOffsetIso}`,
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: {
          "@type": "Place",
          name: display,
          address: {
            "@type": "PostalAddress",
            addressLocality: location.name,
            addressRegion: province?.name ?? "Indonesia",
            addressCountry: "ID",
          },
          geo: { "@type": "GeoCoordinates", latitude: location.lat, longitude: location.lng },
        },
        organizer: {
          "@type": "Organization",
          name: "Kementerian Agama Republik Indonesia",
          url: "https://kemenag.go.id",
        },
        performer: {
          "@type": "Organization",
          name: "Kementerian Agama Republik Indonesia",
          url: "https://kemenag.go.id",
        },
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "IDR",
          availability: "https://schema.org/InStock",
          url: `https://hari.co.id${canonicalPath}`,
          validFrom: r.iso,
        },
        image: ["https://hari.co.id/og-default.svg"],
        url: `https://hari.co.id${canonicalPath}`,
      },
    })),
  };

  const webPageLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `Jadwal Buka Puasa ${display}`,
    url: `https://hari.co.id${canonicalPath}`,
    inLanguage: "id-ID",
    isPartOf: { "@type": "WebSite", name: "Hari.co.id", url: "https://hari.co.id/" },
    about: prayerEntities(),
    speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", ".font-display"] },
  };

  // Sibling kecamatan suggestions for kota pages.
  const siblings = location.kind === "kota"
    ? getKecamatanInKota(location.slug).slice(0, 12)
    : [];
  const provinceCities = location.kind === "provinsi"
    ? KOTA_ALL.filter((k) => k.provinceSlug === location.slug)
    : [];
  const allSiblings = location.kind === "kota" ? getKecamatanInKota(location.slug) : [];

  return (
    <SiteShell>
      <div className="mx-auto max-w-4xl px-4 py-8">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(placeLd) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(eventLd) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(datasetLd) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListLd) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageLd) }} />
        <PrayerBreadcrumb variant="buka-puasa" location={location} />
        <PrayerCrossNav active="buka-puasa" location={location} />

        <h1 className="font-display text-3xl tracking-tight md:text-4xl">
          Jadwal Buka Puasa {display}
        </h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          Waktu berbuka puasa di {display} = waktu maghrib menurut metode
          Kementerian Agama RI. Halaman ini juga menampilkan waktu imsak dan
          subuh untuk membantu Anda menyiapkan sahur.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <FavoriteButton variant="buka-puasa" location={location} />
          <PrayerShareDialog variant="buka-puasa" location={location} />
          <PrayerPdfButton variant="buka-puasa" location={location} defaultMode={mode === "year" ? "year" : "month"} year={view.y} month={view.m} day={today.d} />
        </div>
        <div className="mt-4">
          <LocationQuickSearch variant="buka-puasa" placeholder="Cari lokasi buka puasa lain..." />
        </div>

        {/* HERO buka puasa card — emphasises maghrib */}
        <section className="mt-6 rounded-xl border border-prayer/30 bg-prayer/5 p-6">
          <p className="text-xs uppercase tracking-wide text-prayer">Waktu berbuka hari ini</p>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="font-display text-5xl text-prayer">{todayTimes.maghrib}</span>
            <span className="text-sm text-muted-foreground">
              ({tzLabel(location.tz)})
            </span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {DAY_NAMES_SHORT_ID[dayOfWeek(today.y, today.m, today.d)]},{" "}
            {today.d} {monthName} {today.y} — {hijri.day}{" "}
            {HIJRI_MONTHS[hijri.month - 1]} {hijri.year} H
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Imsak" value={todayTimes.imsak} />
            <Stat label="Subuh" value={todayTimes.subuh} />
            <Stat label="Maghrib (Buka)" value={todayTimes.maghrib} highlight />
            <Stat label="Isya" value={todayTimes.isya} />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Koordinat: {location.lat.toFixed(3)}, {location.lng.toFixed(3)} ·
            Metode Kemenag RI (Subuh 20°, Isya 18°, ihtiyat 2 menit).
          </p>
        </section>

        {/* View toggle: Bulan / Tahun (parity with sholat & imsak) */}
        <div className="mt-4 flex items-center gap-2">
          <div className="inline-flex rounded-md border border-border bg-card p-0.5">
            <button type="button" onClick={() => setMode("month")} className={cn("inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm", mode === "month" ? "bg-prayer text-prayer-foreground" : "hover:bg-accent")}>
              <CalendarDays className="h-4 w-4" /> Bulan
            </button>
            <button type="button" onClick={() => setMode("year")} className={cn("inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm", mode === "year" ? "bg-prayer text-prayer-foreground" : "hover:bg-accent")}>
              <CalendarRange className="h-4 w-4" /> Tahun {view.y}
            </button>
          </div>
        </div>

        {mode === "month" ? (
        <section className="mt-3">
          <div className="flex items-center justify-between gap-3 rounded-t-lg border border-b-0 border-border bg-card p-3">
            <button type="button" onClick={prevMonth} aria-label="Bulan sebelumnya" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-sm hover:bg-accent">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <h2 className="text-base font-semibold">
              Jadwal Buka Puasa {monthName} {view.y}
            </h2>
            <button type="button" onClick={nextMonth} aria-label="Bulan berikutnya" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-sm hover:bg-accent">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <p className="border-x border-border bg-card px-3 pb-2 text-xs text-muted-foreground">
            Waktu berbuka (maghrib) untuk {display} sebulan penuh.
          </p>
          <div className="overflow-x-auto rounded-b-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Tanggal</th>
                  <th className="px-3 py-2 text-right">Imsak</th>
                  <th className="px-3 py-2 text-right">Subuh</th>
                  <th className="px-3 py-2 text-right text-prayer">Buka (Maghrib)</th>
                  <th className="px-3 py-2 text-right">Isya</th>
                </tr>
              </thead>
              <tbody>
                {month.map((r) => {
                  const p = parseISO(r.iso)!;
                  const isToday = r.iso === todayISO();
                  return (
                    <tr
                      key={r.iso}
                      className={isToday ? "bg-prayer/10 font-medium" : "border-t border-border"}
                    >
                      <td className="px-3 py-1.5">
                        {DAY_NAMES_SHORT_ID[dayOfWeek(p.y, p.m, p.d)]}, {p.d} {MONTH_NAMES_ID[p.m - 1]}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{r.times.imsak}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{r.times.subuh}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-prayer">{r.times.maghrib}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{r.times.isya}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
        ) : (
          <div className="mt-3">
            <h2 className="mb-2 text-xl font-semibold">Jadwal Buka Puasa {view.y} - {display}</h2>
            <Suspense fallback={<div style={{ minHeight: 600 }} />}>
              <YearView location={location} year={view.y} cols={[...BUKA_PUASA_COLS] as never} />
            </Suspense>
            <p className="mt-3 text-xs text-muted-foreground">
              Tampilan setahun {view.y}. Ketuk header bulan untuk lipat/buka. Gunakan tombol PDF di atas untuk export setahun.
            </p>
          </div>
        )}

        {(allSiblings.length > 0 || provinceCities.length > 0) && (
          <section className="mt-10">
            {allSiblings.length > 0 && (
              <RelatedAreaGrid variant="buka-puasa" parent={location} items={allSiblings} childKind="kecamatan" />
            )}
            {provinceCities.length > 0 && (
              <RelatedAreaGrid variant="buka-puasa" parent={location} items={provinceCities} childKind="kota" />
            )}
          </section>
        )}

        <BukaPuasaFacts location={location} />

        <section className="mt-10 rounded-lg border border-border bg-card p-6 leading-7 md:leading-8">
          <h2 className="mb-3 text-lg font-semibold text-foreground">{copy.introHeading}</h2>
          <p className="text-base text-foreground/85">{copy.introBody}</p>
          <h3 className="mt-6 mb-3 text-base font-semibold text-foreground">{copy.tipsHeading}</h3>
          <ul className="ml-5 list-disc space-y-2 text-base text-foreground/85">
            {copy.tips.map((tip) => <li key={tip}>{tip}</li>)}
          </ul>
        </section>

        {/* Unique pSEO content blocks */}
        <section className="mt-10 grid gap-4 md:grid-cols-2">
          <article className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold">Tips Berbuka Puasa di {location.name}</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>Berbuka dengan kurma & air putih sesuai sunnah Nabi sebelum makan berat.</li>
              <li>
                Hindari minuman dingin berlebihan agar lambung tidak kaget setelah ±13 jam puasa.
              </li>
              <li>
                Jadwal {display} berdasarkan koordinat {location.lat.toFixed(2)}, {location.lng.toFixed(2)};
                kecamatan terdekat bisa berbeda 1–2 menit.
              </li>
              <li>Sholat maghrib tepat waktu setelah berbuka, lalu lanjut tarawih bersama.</li>
            </ul>
          </article>
          <article className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold">FAQ Buka Puasa</h2>
            <dl className="mt-2 space-y-3 text-sm">
              <div>
                <dt className="font-medium">Pukul berapa buka puasa di {display} hari ini?</dt>
                <dd className="text-muted-foreground">
                  Pukul {todayTimes.maghrib} {tzLabel(location.tz)} (waktu maghrib menurut Kemenag RI).
                </dd>
              </div>
              <div>
                <dt className="font-medium">Apa beda jadwal buka puasa dan jadwal imsakiyah?</dt>
                <dd className="text-muted-foreground">
                  Jadwal imsakiyah memuat seluruh waktu sholat sehari (imsak–isya).
                  Halaman ini fokus pada waktu berbuka (maghrib) untuk akses cepat.
                </dd>
              </div>
              <div>
                <dt className="font-medium">Apakah jadwal sama untuk seluruh kecamatan?</dt>
                <dd className="text-muted-foreground">
                  Selisih biasanya kurang dari 2 menit. Untuk akurasi optimal,
                  pilih halaman kecamatan Anda di bawah.
                </dd>
              </div>
            </dl>
          </article>
        </section>

        {/* Kecamatan suggestions for kota pages */}
        {siblings.length > 0 && (
          <section className="mt-10">
            <h2 className="text-xl font-semibold">
              Kecamatan di {location.name}
            </h2>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 md:grid-cols-3">
              {siblings.map((kec) => (
                <Link
                  key={kec.slug}
                  to={`/jadwal-buka-puasa/${location.slug}/${kec.slug}/` as never}
                  className="rounded-md border border-border bg-card px-3 py-2 text-sm hover:border-prayer"
                >
                  Buka Puasa {kec.name}
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-8">
          <SourcesNote variant="prayer" />
        </div>
        <PrayerLocationToolsRail variant="buka-puasa" />
      </div>
    </SiteShell>
  );
}

function BukaPuasaFacts({ location }: { location: Location }) {
  const facts = buildLocationFacts("buka-puasa", location);
  return (
    <section className="mt-10 rounded-lg border border-border bg-card p-5">
      <h2 className="mb-3 font-display text-lg">Profil Lokasi Buka Puasa</h2>
      <p className="text-sm text-muted-foreground">{facts.paragraph}</p>
      <dl className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {facts.facts.map((f) => (
          <div key={f.label} className="rounded-md bg-background/60 p-3">
            <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{f.label}</dt>
            <dd className="mt-0.5 text-sm font-medium">{f.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={"rounded-md border px-3 py-2 " + (highlight ? "border-prayer bg-prayer/10" : "border-border bg-background")}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={"font-display text-xl tabular-nums " + (highlight ? "text-prayer" : "text-foreground")}>
        {value}
      </div>
    </div>
  );
}

void PRAYER_LABELS_ID;